import { useAuth } from "@clerk/clerk-react";
import { Box, Fab, GlobalStyles, useMediaQuery, useTheme } from "@mui/material";
import { AutogeneratedDraftsContext } from "contexts/AutogenerateDrafts";
import { BrandsContext } from "contexts/Brands";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { SavedBrandCollectionsContext } from "contexts/SavedBrandCollections";
import { SlideAnimationProvider } from "contexts/SlideAnimation";
import { SubscriptionContext } from "contexts/Subscription";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { UserRequestsContext } from "contexts/UserRequests";
import { useContext, useEffect, useRef, useState } from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { SearchParams, UpgradeDialogSource, VAStatus } from "schemas/dashboard";
import { routes } from "schemas/routes";
import { barWidth } from "styles/theme";

import Alert from "components/Alert";
import Loading from "components/Loading";
import PermanentDrawer from "components/PermanentDrawer";
import { ReportWrongInfoDialog } from "components/ReportWrongInfoDialog";
import HomeSideBar from "features/Home/Sidebar";
import DetailedBrandView from "features/Influencer/Discover/DetailedBrandView";
import DraftEmailBar from "features/Influencer/Discover/DraftBar";
import QuickSendEmailBar from "features/Influencer/Discover/EmailBar";
import EmailVolumePromoDialog from "features/Influencer/Discover/Results/Dialogs/EmailVolumePromoDialog";
import CompleteOnboardingDialog from "features/Influencer/HomePage/CompleteOnboardingDialog";
import CreateTemplateDialog from "features/Influencer/HomePage/CreateTemplateDialog";
import EmailBrandDialog from "features/Influencer/HomePage/EmailBrandDialog";
import InstagramOnboardingDialog from "features/Influencer/HomePage/OnboardingStepper/InstagramOnboardingDialog";
import ProfileDialog from "features/Influencer/HomePage/ProfileDialog";
import QuickSendIndividualDrawer from "features/Influencer/QuickSendIndividualDrawer";
import ComposeDraftDrawer from "features/Influencer/QuickSendIndividualDrawer/ComposeDraftDrawer";
import CreateTemplate from "features/Influencer/Templates/TemplatesTable/CreateTemplate";
import EditTemplateDialog from "features/Influencer/Templates/TemplatesTable/EditTemplate";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import {
  DISPLAYED_EMAIL_PROMO_DIALOG,
  DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL,
  DISPLAYED_ONBOARDING_PAYWALL,
  FEEDBACK_GIVEN,
  HIDE_REFERRAL_DIALOG,
} from "utils/localStorage";
import { identifyUser, trackEvent } from "utils/tracking";
import { useAlert } from "utils/useAlert";
import { usePageSize } from "utils/usePageSize";

import HomeBanner from "./Banner";
import CustomDomainDialog from "./CustomDomainDialog";
import LeftSidebar from "./LeftSidebar";
import BugReportDialog from "./LeftSidebar/BugReportDialog";
import ReferralDialog from "./ReferralDialog";
import FavoritesList from "./Sidebar/FavoritesList";
import TopNavigation from "./TopNavigation";
import { UpgradeDialog } from "./UpgradeDialog";
import { NotInterested } from "./UpgradeDialog/NotInterested";
import CancelDialog from "./UpgradeDialog/Plan/CancelDialog";
import ManageDialog from "./UpgradeDialog/Plan/ManagePlanDialog";
import styles from "./styles";

export default function Home() {
  const [message, severity, setAlert, closeAlert] = useAlert();
  const { getToken, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { fetchOutreachTemplates } = useContext(OutreachTemplatesContext);
  const { openReportInfoDialog, setOpenReportInfoDialog } =
    useContext(BrandsContext);

  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { isLargeRecommendedPage, hasBanner } = usePageSize();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  // Contexts
  const {
    currentUser,
    currentOrg,
    setCurrentOrg,
    setCurrentUser,
    setUserIntegrations,
    setUserNotifications,
    setProfile,
    setEmailSettings,
    setExcludedBentoSearchFilterIds,
    setIncludedBentoSearchFilterIds,
    setOrganizationVaProfile,
  } = useContext(OrganizationUserContext);
  const { draftOpen, brandDetailsId } = useContext(QuickSendDrawerContext);
  const {
    createTemplateDialogOpen,
    setEditTemplateDialogId,
    editTemplateDialogId,
  } = useContext(OutreachTemplatesContext);
  const { fetchCollections } = useContext(SavedBrandCollectionsContext);
  const { fetchUserRequest } = useContext(UserRequestsContext);
  const {
    plan,
    subscription,
    setSubscription,
    upgradeDialogSource,
    setUpgradeDialogSource,
    setReferralDialog,
    referralDialog,
    setCustomDomainDialog,
    customDomainDialog,
    cancelDialog,
    manageDialog,
    setManageDialog,
    setCancelDialog,
  } = useContext(SubscriptionContext);

  const {
    profileDialogOpen,
    instagramOnboardingDialogOpen,
    createTemplateOnboardingDialogOpen,
    emailBrandDialogOpen,
    completeOnboardingDialogOpen,
  } = useContext(OnboardingDialogsContext);
  const {
    emailHealth,
    fetchIntegrationLoading,
    fetchIntegrationHealth,
    openIntegrationDialog,
    setOpenIntegrationDialog,
  } = useContext(UserIntegrationsContext);
  const { fetchDraftBentoBrandIds } = useContext(AutogeneratedDraftsContext);
  const { discoverTab, tabWidth, setDiscoverTab } =
    useContext(DiscoverViewContext);
  const hasAlreadyGivenFeedback =
    localStorage.getItem(`${FEEDBACK_GIVEN}-${currentOrg?.id}`) === "true";
  const openConnectToGmail =
    searchParams.get("open_integration_dialog") === "true";
  const hideReferralDialog =
    localStorage.getItem(`${HIDE_REFERRAL_DIALOG}-${currentOrg?.id}`) ===
    "true";
  const affiliateCode = searchParams.get(SearchParams.AFFILIATE_CODE);

  const [fetchUserLoading, setFetchUserLoading] = useState<boolean>(true);
  const [notInterestedOpen, setNotInterestedOpen] = useState(false);
  const [openEmailPromoDialog, setOpenEmailPromoDialog] = useState(false);

  const [openBugDialog, setOpenBugDialog] = useState(false);

  const [totalEmails, setTotalEmails] = useState<number | undefined>(undefined);

  const handleReportBug = () => {
    setOpenBugDialog(true);
    trackEvent("Bug Report Dialog Shown", {
      Source: "Floating Action Button",
    });
  };

  const fetchUser = async () => {
    setFetchUserLoading(true);

    if (currentUser && currentOrg?.id) {
      const subscribeQuery = searchParams.get("subscribe");
      if (subscribeQuery) {
        trackEvent("Visited Page with Subscribe Dialog", {
          params: subscribeQuery,
        });
        setUpgradeDialogSource(UpgradeDialogSource.link_query);
      }

      await fetchIntegrationHealth(currentOrg.id);
      setFetchUserLoading(false);
      return;
    }

    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/auth/user?affiliate_code=${affiliateCode || ""}`,
      );
      const { niches } = await fetcherAuth(
        getToken,
        `/api/organization/${res.organization.id}/organization-niches`,
      );
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setOrganizationVaProfile({
        ...res.organizationVaProfile,
        niches,
      });
      setCurrentOrg(res.organization);
      setSubscription(res.subscription);
      setUserIntegrations(res.userIntegrations);
      setUserNotifications(res.userNotifications);
      setEmailSettings(res.emailSetting);
      setIncludedBentoSearchFilterIds(res.includedBentoSearchFilterIds);
      setExcludedBentoSearchFilterIds(res.excludedBentoSearchFilterIds);
      setProfile(res.organizationProfile);
      if (
        res.organization.vaStatus === VAStatus.pending &&
        res.organization.isVaUser
      ) {
        navigate(`/${routes.onboardingVaSelfServe}${location.search}`);
        return;
      }
      const subscribeQuery = searchParams.get("subscribe");
      if (subscribeQuery) {
        trackEvent("Visited Page with Subscribe Dialog", {
          params: subscribeQuery,
        });
        setUpgradeDialogSource(UpgradeDialogSource.link_query);
      }
      await fetchIntegrationHealth(res.organization.id);
    } catch (error) {
      // the user might not be created, go to onboarding
      navigate(`/${routes.onboardingInfluencer}${location.search}`);
    } finally {
      setFetchUserLoading(false);
    }
  };

  useEffect(() => {
    if (isSignedIn) {
      fetchUser();
    } else {
      setFetchUserLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  useEffect(() => {
    if (currentOrg?.id) {
      fetchOutreachTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    const checkoutInfo = searchParams.get("checkout");

    if (checkoutInfo === "success") {
      if (isPage(routes.inboxCheckins)) {
        setAlert(
          "Thank you for subscribing to Bento. We are generating more email drafts for you. We will send you an email once they are ready!",
          "success",
        );
      } else {
        setAlert(
          "Thank you for subscribing to Bento. You can visit our settings page to manage your subscription!",
          "success",
        );
      }
    } else if (checkoutInfo === "add_on_success") {
      setAlert("Thank you for your purchase.", "success");
    } else if (checkoutInfo === "failed_payment") {
      setAlert(
        "Your payment failed. Please try again or contact support at hello@onbento.com for assistance.",
        "error",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (
      !fetchIntegrationLoading &&
      !emailHealth &&
      totalEmails &&
      totalEmails > 0
    ) {
      setOpenIntegrationDialog(true);
    } else if (emailHealth && openConnectToGmail) {
      setAlert("You have successfully connected to Gmail!", "success");
      searchParams.delete("open_integration_dialog");
      setSearchParams(searchParams);
    } else if (!emailHealth && openConnectToGmail) {
      setOpenIntegrationDialog(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailHealth, fetchIntegrationLoading, currentOrg?.id, totalEmails]);

  useEffect(() => {
    if (currentOrg?.isVaUser && subscription?.subscription?.startDate) {
      const isOldSubscriber = subscription?.subscription?.startDate
        ? new Date(subscription.subscription.startDate) < new Date("2025-11-17")
        : false;
      const alreadyDisplayedEmailDialog =
        localStorage.getItem(
          `${DISPLAYED_EMAIL_PROMO_DIALOG}-${currentOrg?.id}`,
        ) === "true";

      const hasEmailVolume = Boolean(
        subscription?.emailVolumes && subscription?.emailVolumes?.length > 0,
      );

      if (!alreadyDisplayedEmailDialog && !hasEmailVolume && isOldSubscriber) {
        localStorage.setItem(
          `${DISPLAYED_EMAIL_PROMO_DIALOG}-${currentOrg?.id}`,
          "true",
        );
        setOpenEmailPromoDialog(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.isVaUser]);

  const handleCloseUpgradeDialog = () => {
    if (
      !hasAlreadyGivenFeedback &&
      subscription &&
      subscription?.remainingMessages <= 0
    ) {
      setNotInterestedOpen(true);
    }
    setUpgradeDialogSource(null);
  };

  const handleCloseNotInterestedDialog = () => {
    const givenFeedback =
      localStorage.getItem(`${FEEDBACK_GIVEN}-${currentOrg?.id}`) === "true";
    if (givenFeedback) {
      setUpgradeDialogSource(null);
      if (upgradeDialogSource === UpgradeDialogSource.onboarding_paywall) {
        localStorage.setItem(
          `${DISPLAYED_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
          "true",
        );
      }
      if (
        upgradeDialogSource ===
        UpgradeDialogSource.instagram_onboarding_paywall_pro
      ) {
        localStorage.removeItem(
          `${DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
        );
      }
    }
    setNotInterestedOpen(false);
    searchParams.delete("checkout");
    setSearchParams(searchParams);
    if (upgradeDialogSource) setUpgradeDialogSource(null);
  };

  const getTotalEmailsCount = async () => {
    if (!currentOrg?.id) {
      return;
    }
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/emails/total-count`,
      );
      setTotalEmails(res.count);
      if (
        res.count > 10 &&
        !hideReferralDialog &&
        !referralDialog &&
        !openIntegrationDialog
      ) {
        setReferralDialog(true);
      }
    } catch (error) {
      setAlert(
        error?.message || "Something went wrong. Please reload and try again",
        "error",
      );
    }
  };

  const openCancel = () => {
    trackEvent(
      "Manage Subscription Dialog Cancel Subscription Button Pressed",
      {
        plan,
      },
    );
    setManageDialog(false);
    setCancelDialog(true);
  };

  const closeManageDialog = () => {
    trackEvent("Manage Subscription Dialog Closed", {
      plan,
    });
    setManageDialog(false);
  };

  const closeCancelDialog = () => {
    trackEvent("Cancel Subscription Dialog Closed", {
      plan,
    });
    setCancelDialog(false);
  };

  useEffect(() => {
    fetchCollections();
    fetchUserRequest(true);
    fetchUserRequest(false);
    fetchDraftBentoBrandIds();
    getTotalEmailsCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [location.pathname]);

  const hideWhiteBackground =
    (isPage(routes.virtualAssistant) &&
      !currentOrg?.isVaUser &&
      !isMobileScreen) ||
    isPage(routes.home);

  return fetchUserLoading ? (
    <Loading />
  ) : (
    <Box sx={styles.root}>
      <GlobalStyles styles={{ body: styles.root }} />

      {location.pathname.includes("portfolio-sites") &&
      location.pathname.includes("edit/design") ? (
        <Outlet />
      ) : (
        <>
          {!isMobileScreen && <LeftSidebar />}
          {!currentOrg?.isBrand && <HomeBanner />}

          <TopNavigation />
          <Box
            ref={scrollRef}
            sx={[
              styles.body(hasBanner, hideWhiteBackground),
              {
                right: {
                  xs: 0,
                  md:
                    discoverTab !== null
                      ? `${tabWidth + barWidth}px`
                      : `${barWidth}px`,
                },
              },
            ]}
          >
            <Box sx={styles.wrapper}>
              <Outlet />
            </Box>
            {!isMobileScreen && <HomeSideBar />}
          </Box>
          {isMobileScreen && (
            <FavoritesList closeDialog={() => setDiscoverTab(null)} />
          )}

          {/* Recommendation page has their own display of QuickSend drawer and brand details view */}
          {!isLargeRecommendedPage && !emailBrandDialogOpen && (
            <>
              {!isMobileScreen && <QuickSendEmailBar />}
              <QuickSendIndividualDrawer />
              {brandDetailsId > -1 && !currentOrg?.isBrand && (
                <DetailedBrandView />
              )}
            </>
          )}

          {!isMobileScreen && <DraftEmailBar />}
          {draftOpen && <ComposeDraftDrawer />}

          {!isMobileScreen && (
            <PermanentDrawer
              open={discoverTab !== null}
              drawerWidth={tabWidth}
            />
          )}
          {createTemplateDialogOpen && <CreateTemplate />}
          {editTemplateDialogId && (
            <EditTemplateDialog
              propsTemplateId={editTemplateDialogId}
              propsHandleClose={(e?: CustomEvent) => {
                e?.stopPropagation();
                setEditTemplateDialogId(null);
              }}
            />
          )}
        </>
      )}

      <UpgradeDialog
        open={!!upgradeDialogSource}
        onClose={handleCloseUpgradeDialog}
        setAlert={setAlert}
      />

      <EmailVolumePromoDialog
        open={openEmailPromoDialog}
        handleClose={() => {
          localStorage.setItem(
            `${DISPLAYED_EMAIL_PROMO_DIALOG}-${currentOrg?.id}`,
            "true",
          );
          setOpenEmailPromoDialog(false);
        }}
      />

      <ReferralDialog
        open={referralDialog}
        close={() => {
          setReferralDialog(false);
          if (!hideReferralDialog) {
            localStorage.setItem(
              `${HIDE_REFERRAL_DIALOG}-${currentOrg?.id}`,
              "true",
            );
          }
        }}
      />

      <NotInterested
        open={notInterestedOpen || searchParams.get("checkout") === "failed"}
        close={handleCloseNotInterestedDialog}
        setAlert={setAlert}
      />

      <Alert message={message} severity={severity} closeAlert={closeAlert} />

      {!isMobileScreen && (
        <Fab
          variant="extended"
          sx={{
            position: "absolute",
            bottom: 15,
            right: discoverTab !== null ? 365 : 15,
            backgroundColor: "primary.main",
            color: "white",
            textTransform: "none",
            fontSize: 15,
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
          onClick={handleReportBug}
          size="small"
        >
          <Box component="i" className="fa-regular fa-question" />
        </Fab>
      )}

      {profileDialogOpen && (
        <SlideAnimationProvider>
          <ProfileDialog />
        </SlideAnimationProvider>
      )}
      {createTemplateOnboardingDialogOpen && (
        <SlideAnimationProvider>
          <CreateTemplateDialog />
        </SlideAnimationProvider>
      )}
      {emailBrandDialogOpen && (
        <SlideAnimationProvider>
          <EmailBrandDialog />
        </SlideAnimationProvider>
      )}
      {instagramOnboardingDialogOpen && (
        <SlideAnimationProvider>
          <InstagramOnboardingDialog />
        </SlideAnimationProvider>
      )}
      {completeOnboardingDialogOpen && <CompleteOnboardingDialog />}

      <BugReportDialog
        open={openBugDialog}
        handleClose={() => setOpenBugDialog(false)}
      />

      <ReportWrongInfoDialog
        open={!!openReportInfoDialog}
        close={() => setOpenReportInfoDialog(null)}
        brand={openReportInfoDialog}
      />

      <CustomDomainDialog
        open={customDomainDialog}
        handeClose={() => {
          setCustomDomainDialog(false);
        }}
      />

      <ManageDialog
        open={manageDialog}
        onClose={closeManageDialog}
        openCancel={openCancel}
      />

      <CancelDialog open={cancelDialog} onClose={closeCancelDialog} />
    </Box>
  );
}
